import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { clearPublicSettingsCache } from "../../hooks/usePublicSettings";
import { saveSuccess, saveError } from "../../lib/saveBanner";

/**
 * Company / Brand name (v4.4.3 — moved from Rental Settings to System
 * Settings). Same state, handler and PUT /admin/settings payload as before.
 */
export default function BrandSection() {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings")
      .then((r) => setName(r.data?.company_name || ""))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data: current } = await api.get("/admin/settings");
      const { data } = await api.put("/admin/settings", {
        ...current, company_name: name.trim(),
      });
      setName(data?.company_name || "");
      clearPublicSettingsCache();
      saveSuccess();
    } catch (err) {
      saveError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded-lg overflow-hidden"
      data-testid="brand-panel"
    >
      <header className="px-6 py-4 border-b border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827]">
        <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Section</p>
        <h2 className="font-display text-xl mt-1">Company / Brand</h2>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
          Shown top-left in the admin console, in the public footer, and on rental agreements.
        </p>
      </header>
      <div className="p-6 flex flex-col md:flex-row gap-4 md:items-end">
        <label className="flex-1">
          <span className="block text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400 mb-2">
            Company / brand name
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Brainart Master Demo Rent a Car"
            disabled={loading || saving}
            data-testid="brand-name-input"
            className="w-full bg-transparent border border-black/15 dark:border-white/15 rounded px-3 py-2 text-sm focus:outline-none focus:border-black dark:focus:border-white"
          />
        </label>
        <button
          type="button"
          onClick={save}
          disabled={loading || saving}
          data-testid="brand-name-save"
          className="bg-black text-white dark:bg-white dark:text-black text-xs px-5 py-2.5 rounded inline-flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          Save brand
        </button>
      </div>
    </section>
  );
}
